import { CATEGORIES, dec, formatMoney, plural, sum, ZERO } from "@bl/domain";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ClipboardCheck, MapPin, PackageMinus, Plus, Scale, Search, Truck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type Item, useItems, useLocations } from "../api";
import { useMedia } from "../components/DocumentViewer";
import {
  Empty,
  ErrorNote,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import { unrecordedCounts } from "../countDraft";
import { AddItemDialog } from "../forms/ItemDialogs";
import { MovementDialog, type MovementMode } from "../forms/MovementDialog";
import { useLayout } from "../layout/LayoutProvider";
import type { SortKey } from "../layout/layout";
import { itemMatches } from "../search";
import { rowsOf } from "./hopVarieties";
import { Cards, GroupToggle, type Inv, Overview } from "./InventoryCards";
import { FilterMenu, OptionsMenu, PhoneViewMenu, SortMenu, ViewMenu } from "./InventoryMenus";
import { Table } from "./InventoryTable";

function sortItems(items: Item[], sort: Inv["sort"]): Item[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const by: Record<SortKey, (a: Item, b: Item) => number> = {
    name: (a, b) => a.displayName.localeCompare(b.displayName),
    category: (a, b) =>
      CATEGORIES.indexOf(a.category) - CATEGORIES.indexOf(b.category) ||
      a.displayName.localeCompare(b.displayName),
    // Across different units a raw comparison means little, but within a category it orders sensibly.
    onHand: (a, b) => dec(a.onHand.quantity).cmp(dec(b.onHand.quantity)),
    lastMovement: (a, b) => (a.lastMovementAt ?? "").localeCompare(b.lastMovementAt ?? ""),
  };
  return [...items].sort((a, b) => by[sort.key](a, b) * dir);
}
function useVisibleItems(inv: Inv, query: string) {
  const { data: items = [], ...rest } = useItems();
  const visible = useMemo(() => {
    const filtered = items.filter((i) => {
      if (!itemMatches(i, query)) return false;
      if (inv.category && i.category !== inv.category) return false;
      if (inv.locationId && !i.onHand.byLocation.some((b) => b.locationId === inv.locationId)) {
        return false;
      }
      if (inv.hideEmpty && dec(i.onHand.quantity).eq(ZERO)) return false;
      return true;
    });
    return sortItems(filtered, inv.sort);
  }, [items, inv, query]);
  return { items, visible, ...rest };
}
/**
 * Sets Inventory to show what is in one location (Scott, 2026-10-02: "why cant i click on locations to see
 * what is in them?"). It sets the page's location filter and clears its category, so the whole room
 * shows; the filter stays on until it is cleared, as one set from the Filter menu does.
 */
export function useShowLocation() {
  const { update } = useLayout();
  return (locationId: string) =>
    update((l) => ({ ...l, inventory: { ...l.inventory, locationId, category: null } }));
}
/** Stock at the filtered location only, when a location filter is on. */
function scoped(item: Item, locationId: string | null): Item {
  if (!locationId) return item;
  const here = item.onHand.byLocation.filter((b) => b.locationId === locationId);
  const b = here[0];
  return {
    ...item,
    onHand: {
      ...item.onHand,
      quantity: b?.quantity ?? "0",
      packs: b?.packs ?? "0",
      loose: b?.loose ?? "0",
      brands: b?.brands ?? [],
      regions: b?.regions ?? [],
      byLocation: here,
    },
  };
}
export function InventoryPage() {
  const { layout, update } = useLayout();
  const inv = layout.inventory;
  const setInv = (patch: Partial<Inv>) =>
    update((l) => ({ ...l, inventory: { ...l.inventory, ...patch } }));
  const { data: locations = [] } = useLocations();
  // Inventory's own search (Tyler, 2026-10-03): what does not match drops away as it is typed, and
  // comes back as it is erased. It is kept in the address, so Back from an item returns to it.
  const navigate = useNavigate();
  const { q: asked = "" } = useSearch({ from: "/" });
  const [query, setQuery] = useState(asked);
  useEffect(() => {
    navigate({
      to: "/",
      search: query ? { q: query } : {},
      replace: true,
      resetScroll: false,
    });
  }, [query, navigate]);
  const searching = query.trim() !== "";
  const { items, visible, isLoading, error } = useVisibleItems(inv, query);
  const [adding, setAdding] = useState(false);
  const [dialog, setDialog] = useState<MovementMode | null>(null);
  const phone = useMedia("(max-width: 760px)");
  // The Filter menu's own filters; the search narrows the page too, but is not one of them.
  const filtered = inv.category !== null || inv.locationId !== null || inv.hideEmpty;
  const narrowed = searching || filtered;
  const place = inv.locationId ? locations.find((l) => l.id === inv.locationId) : undefined;
  const unrecorded = place ? unrecordedCounts(place.id) : 0;
  usePageTitle(place?.name, "Inventory");

  const groups = useMemo(() => {
    if (!inv.groupByCategory)
      return [{ key: "all", items: visible.map((i) => scoped(i, inv.locationId)) }];
    return CATEGORIES.map((c) => ({
      key: c,
      items: visible.filter((i) => i.category === c).map((i) => scoped(i, inv.locationId)),
    })).filter((g) => g.items.length > 0);
  }, [visible, inv.groupByCategory, inv.locationId]);
  // A category folds up under its heading and drops back down, in every view (Tyler, 2026-10-02).
  // While searching, every category with a match is open: a match is never hidden in a fold.
  const folded = (key: string) =>
    inv.groupByCategory && !searching && (inv.collapsed[key] ?? false);
  const fold = (key: string) => setInv({ collapsed: { ...inv.collapsed, [key]: !folded(key) } });

  const negative = items.filter((i) => i.onHand.negative).length;
  const value = sum(visible.map((i) => i.cost.valueOnHand));
  const stocked = items.filter((i) => !dec(i.onHand.quantity).eq(ZERO)).length;

  return (
    <>
      <Toolbar
        left={
          <>
            <ToolbarButton
              icon={<Plus size={20} />}
              label="Add item"
              onClick={() => setAdding(true)}
            />
            <ToolbarSeparator />
            <ToolbarButton
              icon={<Truck size={20} />}
              label="Receive"
              labeled
              onClick={() => setDialog("receive")}
            />
            <ToolbarButton
              icon={<PackageMinus size={20} />}
              label="Use"
              labeled
              onClick={() => setDialog("use")}
            />
            <ToolbarButton
              icon={<ClipboardCheck size={20} />}
              label="Count"
              labeled
              onClick={() => setDialog("adjust")}
            />
          </>
        }
        right={
          phone ? (
            <PhoneViewMenu
              inv={inv}
              locations={locations}
              active={filtered || inv.quantityMode === "pack"}
              onChange={setInv}
            />
          ) : (
            <>
              <ToolbarButton
                icon={<Scale size={20} />}
                label="Packs"
                title="Show amounts in packs (bags, boxes) instead of weight"
                aria-pressed={inv.quantityMode === "pack"}
                onClick={() =>
                  setInv({ quantityMode: inv.quantityMode === "pack" ? "unit" : "pack" })
                }
              />
              <ViewMenu view={inv.view} onChange={(view) => setInv({ view })} />
              <SortMenu sort={inv.sort} onChange={(sort) => setInv({ sort })} />
              <FilterMenu inv={inv} locations={locations} active={filtered} onChange={setInv} />
              <OptionsMenu inv={inv} onChange={setInv} />
            </>
          )
        }
      />
      <div className="page-body">
        {items.length > 0 ? (
          <div className="inventory-search">
            <Search size={18} aria-hidden />
            <input
              type="search"
              className="input"
              placeholder="Search inventory"
              aria-label="Search inventory"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setQuery("");
              }}
            />
          </div>
        ) : null}
        {/* The page's name for a screen reader, which the location heading below takes over when
            Inventory shows one location (the re-critique, 2026-10-06: it had no heading at all). */}
        {place ? null : <h1 className="sr-only">Inventory</h1>}
        {place ? (
          <div className="location-heading">
            <MapPin size={18} aria-hidden />
            <h1>{place.name}</h1>
            <Link to="/count" search={{ location: place.id }} className="button">
              {unrecorded ? `Count sheet: ${unrecorded} not recorded` : "Count everything here"}
            </Link>
            <button
              type="button"
              className="link-button"
              onClick={() => setInv({ locationId: null })}
            >
              Show every location
            </button>
          </div>
        ) : null}
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {!isLoading && items.length === 0 ? (
          <div className="empty first-run">
            <img src="/logo-256.png" alt="" />
            <h2>Your inventory is empty</h2>
            <p>Add your first item from an invoice, or look it up by name or SKU.</p>
            <button type="button" className="button primary" onClick={() => setAdding(true)}>
              <Plus size={16} /> Add an item
            </button>
          </div>
        ) : null}
        {!isLoading && items.length > 0 && visible.length === 0 ? (
          <Empty
            title={
              searching
                ? `Nothing matches "${query.trim()}"`
                : place && !inv.category && !inv.hideEmpty
                  ? `Nothing is in ${place.name}`
                  : "Nothing matches these filters"
            }
          >
            <div className="row empty-actions">
              {searching ? (
                <button type="button" className="button" onClick={() => setQuery("")}>
                  Clear search
                </button>
              ) : null}
              {filtered ? (
                <button
                  type="button"
                  className="button"
                  onClick={() => setInv({ category: null, locationId: null, hideEmpty: false })}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </Empty>
        ) : null}
        {inv.view === "table" && groups.length > 0 ? (
          <Table inv={inv} groups={groups} locations={locations} folded={folded} onFold={fold} />
        ) : (
          groups.map((g) => (
            <section key={g.key} className="item-group">
              {inv.groupByCategory ? (
                <h2 className="group-heading">
                  <GroupToggle
                    category={g.key}
                    count={rowsOf(g.items).length}
                    folded={folded(g.key)}
                    controls={`group-${g.key}`}
                    onToggle={() => fold(g.key)}
                  />
                </h2>
              ) : null}
              {folded(g.key) ? null : (
                <div id={`group-${g.key}`}>
                  {inv.view === "cards" ? (
                    <Cards inv={inv} items={g.items} locations={locations} />
                  ) : (
                    <Overview inv={inv} items={g.items} locations={locations} />
                  )}
                </div>
              )}
            </section>
          ))
        )}
      </div>
      {items.length > 0 ? (
        <footer className="page-footer">
          <span>
            {items.length} {plural("item", String(items.length))}
          </span>
          <span>{stocked} in stock</span>
          {value.gt(ZERO) ? (
            <span>
              {formatMoney(value)} on hand{narrowed ? " (shown items)" : ""}
            </span>
          ) : null}
          {negative ? <span className="negative">{negative} below zero, count them</span> : null}
          {narrowed ? <span>Filtered: showing {visible.length}</span> : null}
        </footer>
      ) : null}
      <AddItemDialog open={adding} onClose={() => setAdding(false)} />
      <MovementDialog
        open={dialog !== null}
        mode={dialog ?? "receive"}
        onClose={() => setDialog(null)}
      />
    </>
  );
}
